import {markAcceptedEntry, returnToAcceptedEntry} from './route-history.js';

/**
 * Decides every hash route change of an app whose current route may hold unsaved work.
 *
 * A route change first asks `mayLeave` whether the route on screen may be left. When it may, the
 * new route is accepted and `show` puts it on screen; when it may not, the address returns to the
 * accepted route's own history entry (see `route-history.js`), so every entry keeps its route.
 *
 * Only one decision runs at a time. A route change that arrives while `mayLeave` is still
 * waiting (the user pressed Back again while the confirm dialog was open) starts no second
 * decision: the running one reads the address once it has an answer, so it leaves for the latest
 * route, or returns to the accepted entry from wherever the address is by then.
 */
export class RouteGuard {
    #mayLeave;
    #show;
    #history;
    #location;
    /** @type {string | null} the route on screen; null while no route is accepted (signed out) */
    #acceptedHash = null;
    /** @type {number | null} the history position of the accepted route's entry; kept on reset */
    #acceptedPosition = null;
    #deciding = false;

    /**
     * @param {object} options
     * @param {(currentHash: string) => Promise<boolean> | boolean} options.mayLeave whether the
     *     route on screen may be left, for example after the user confirms losing unsaved changes
     * @param {(hash: string) => Promise<void> | void} options.show puts an accepted route on screen
     * @param {Pick<History, 'state' | 'length' | 'replaceState' | 'go'>} [options.historyApi]
     * @param {Pick<Location, 'hash'>} [options.locationApi]
     */
    constructor({mayLeave, show, historyApi = history, locationApi = location}) {
        this.#mayLeave = mayLeave;
        this.#show = show;
        this.#history = historyApi;
        this.#location = locationApi;
    }

    /** The route on screen, or null while no route is accepted. */
    get acceptedHash() {
        return this.#acceptedHash;
    }

    /**
     * Accepts the route in the address without asking, for the first route after sign-in.
     * @returns {Promise<void>} settles once the route is on screen
     */
    async accept() {
        const hash = this.#currentHash();
        this.#acceptedHash = hash;
        this.#acceptedPosition = markAcceptedEntry(this.#acceptedPosition, this.#history);
        await this.#show(hash);
    }

    /**
     * Forgets the route on screen, for sign-out. The history position is kept: the entries of
     * the next session follow the ones of this one.
     */
    reset() {
        this.#acceptedHash = null;
    }

    /**
     * Handles a `hashchange`. The change the guard causes itself, back to the accepted entry,
     * names the route on screen and needs nothing.
     * @returns {Promise<void>}
     */
    handleHashChange = async () => {
        if (this.#deciding || this.#currentHash() === this.#acceptedHash) {
            return;
        }
        if (this.#acceptedHash !== null) {
            this.#deciding = true;
            let leave;
            try {
                leave = await this.#mayLeave(this.#acceptedHash);
            } finally {
                this.#deciding = false;
            }
            // The address may have moved on, or back to the route on screen, while waiting.
            if (this.#currentHash() === this.#acceptedHash) {
                return;
            }
            if (!leave) {
                returnToAcceptedEntry(this.#acceptedPosition, this.#history);
                return;
            }
        }
        await this.accept();
    };

    #currentHash() {
        return this.#location.hash || '#/';
    }
}
